import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { apiHelper } from '../utils/apiHelper';

const Container = styled(motion.div)`
  min-height: 100vh;
  background: var(--background-start);
  padding-top: 80px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const RegisterBox = styled.div`
  background: var(--glass-bg);
  backdrop-filter: blur(20px);
  border: 1px solid var(--border-color);
  border-radius: 15px;
  padding: 40px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
`;

const Title = styled.h1`
  color: white;
  text-align: center;
  margin-bottom: 30px;
  font-size: 2rem;
  font-weight: 700;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 15px;
  margin-bottom: 20px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  color: white;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: var(--gradient-pink);
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 12px;
  background: linear-gradient(90deg, var(--gradient-purple), var(--gradient-pink));
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(255, 0, 127, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoginLink = styled(Link)`
  display: block;
  text-align: center;
  margin-top: 20px;
  color: var(--secondary-color);
  text-decoration: none;
  
  &:hover {
    color: var(--gradient-cyan);
  }
`;

const PasswordRequirements = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 20px;
  border: 1px solid var(--border-color);
`;

const Requirement = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 0.85rem;
  color: ${props => props.valid ? 'var(--gradient-cyan)' : 'var(--secondary-color)'};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const RequirementDot = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${props => props.valid ? 'var(--gradient-cyan)' : 'var(--secondary-color)'};
`;

const ErrorMessage = styled.div`
  color: #ff6b6b;
  background: rgba(255, 107, 107, 0.1);
  border: 1px solid rgba(255, 107, 107, 0.3);
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 20px;
  font-size: 0.9rem;
  text-align: center;
`;

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Şifre gereksinimlerini kontrol et
  const passwordRequirements = {
    minLength: formData.password.length >= 8,
    hasUpperCase: /[A-Z]/.test(formData.password),
    hasLowerCase: /[a-z]/.test(formData.password),
    passwordsMatch: formData.password === formData.confirmPassword && formData.password !== ''
  };

  const isPasswordValid = Object.values(passwordRequirements).every(requirement => requirement);

  // Email formatını kontrol et
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const allowedDomains = ['gmail.com', 'outlook.com', 'hotmail.com', 'yahoo.com', 'protonmail.com'];
    
    if (!emailRegex.test(email)) return false;
    
    const domain = email.split('@')[1];
    return allowedDomains.includes(domain);
  };

  // Kullanıcı adı kontrolü
  const isValidUsername = (username) => {
    return /^[a-zA-Z0-9_]{3,20}$/.test(username);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validasyonlar
    if (!isValidUsername(formData.username)) {
      setError('Kullanıcı adı sadece harf, sayı ve alt çizgi içerebilir (3-20 karakter)');
      setLoading(false);
      return;
    }

    if (!isValidEmail(formData.email)) {
      setError('Lütfen geçerli bir email adresi girin (Gmail, Outlook, Yahoo, Hotmail, Protonmail)');
      setLoading(false);
      return;
    }

    if (!isPasswordValid) {
      setError('Lütfen tüm şifre gereksinimlerini karşılayın');
      setLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Şifreler eşleşmiyor');
      setLoading(false);
      return;
    }

    try {
      const response = await apiHelper.register({
        username: formData.username,
        email: formData.email,
        password: formData.password
      });
      const data = await response.json();

      if (data.success) {
        // Başarılı kayıt
        navigate('/');
        window.location.reload();
      } else {
        setError(data.message);
      }
    } catch (error) {
      setError('Kayıt sırasında bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />
      <Container
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <RegisterBox>
          <Title>Kayıt Ol</Title>
          <form onSubmit={handleSubmit}>
            <Input
              type="text"
              placeholder="Kullanıcı Adı"
              value={formData.username}
              onChange={(e) => setFormData({...formData, username: e.target.value})}
              required
              maxLength={20}
            />
            
            <Input
              type="email"
              placeholder="Email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              required
            />
            
            <Input
              type="password"
              placeholder="Şifre"
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
              required
            />
            
            <Input
              type="password"
              placeholder="Şifre Tekrar"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
              required
            />

            <PasswordRequirements>
              <Requirement valid={passwordRequirements.minLength}>
                <RequirementDot valid={passwordRequirements.minLength} />
                En az 8 karakter
              </Requirement>
              <Requirement valid={passwordRequirements.hasUpperCase}>
                <RequirementDot valid={passwordRequirements.hasUpperCase} />
                En az 1 büyük harf (A-Z)
              </Requirement>
              <Requirement valid={passwordRequirements.hasLowerCase}>
                <RequirementDot valid={passwordRequirements.hasLowerCase} />
                En az 1 küçük harf (a-z)
              </Requirement>
              <Requirement valid={passwordRequirements.passwordsMatch}>
                <RequirementDot valid={passwordRequirements.passwordsMatch} />
                Şifreler eşleşiyor
              </Requirement>
            </PasswordRequirements>

            {error && <ErrorMessage>{error}</ErrorMessage>}
            
            <Button type="submit" disabled={loading || !isPasswordValid}>
              {loading ? 'Kayıt Yapılıyor...' : 'Kayıt Ol'}
            </Button>
          </form>
          <LoginLink to="/login">
            Zaten hesabın var mı? Giriş yap
          </LoginLink>
        </RegisterBox>
      </Container>
    </>
  );
};

export default RegisterPage;
